import { IAPIRoutes } from "../../../../config";
import {
  ICompassusMonthlyAssumption,
  ICompassusMonthlyAssumptionsResponse,
  ICompassusMonthlyRecord,
  ICompassusMonthlyRecordsResponse,
  ICompassusPtoRecord,
  ICompassusPtoRecordsResponse,
  ICreateCompassusMonthlyAssumptionPayload,
  ICreateCompassusMonthlyRecordPayload,
  ICreateCompassusPtoRecordPayload,
  IGetCompassusMonthlyAssumptionsParams,
  IUpdateCompassusMonthlyAssumptionPayload,
  IEngagementUtilizationResponse,
  IGetCompassusMonthlyRecordsParams,
  IGetCompassusPtoRecordsParams,
  IGetEngagementUtilizationParams,
  IGetNasaTlxFeedbackParams,
  IGetProductivityParams,
  IUpdateCompassusMonthlyRecordPayload,
  IUpdateCompassusPtoRecordPayload,
  ICompassusProviderProfile,
  IUpdateCompassusProviderProfilePayload,
  INasaTlxFeedbackResponse,
  IProductivityProvidersResponse,
  IProductivityResponse,
  IProductivitySummaryResponse,
  IProductivityUtilizationResponse,
} from "../../../core/ports/driver/compassusDashboardTypes";
import {
  IFacilityNpPatientsResponse,
  IGetFacilityNpPatientsParams,
  IGetNpPatientsParams,
  IGetNpProductivityMeParams,
  IGetNpScheduleParams,
  INpPatientsResponse,
  INpProductivityMeResponse,
  INpScheduleResponse,
} from "../../../core/ports/driver/compassusNpDashboardTypes";

export type ICompassusDashboardRepository = ReturnType<
  typeof CompassusDashboardRepositoryFactory
>;

export const CompassusDashboardRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PATCH } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const productivityParams = ({
    clinicIds,
    year,
    month,
    startDate,
    endDate,
    startMonth,
    endMonth,
  }: IGetProductivityParams) => ({
    ...(clinicIds && clinicIds.length
      ? { clinic_ids: clinicIds.join(",") }
      : {}),
    ...(year ? { year } : {}),
    ...(month ? { month } : {}),
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(startMonth ? { start_month: startMonth } : {}),
    ...(endMonth ? { end_month: endMonth } : {}),
  });

  const getProductivity = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IProductivityResponse> =>
    GET(
      APIRoutes.apiCompassusProductivity,
      productivityParams(params),
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getProductivitySummary = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IProductivitySummaryResponse> =>
    GET(
      APIRoutes.apiCompassusProductivitySummary,
      productivityParams(params),
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getProductivityProviders = async (
    params: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IProductivityProvidersResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityProviders,
      productivityParams(params),
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getProductivityUtilization = async (
    {
      clinicIds,
      year,
      month,
      startDate,
      endDate,
      startMonth,
      endMonth,
      nasaTlxAllTime,
      inactiveDays,
    }: IGetProductivityParams,
    signal?: AbortSignal
  ): Promise<IProductivityUtilizationResponse> =>
    GET(
      APIRoutes.apiCompassusProductivitySnowflakeUtilization,
      {
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
        ...(year ? { year } : {}),
        ...(month ? { month } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(startMonth ? { start_month: startMonth } : {}),
        ...(endMonth ? { end_month: endMonth } : {}),
        ...(nasaTlxAllTime ? { nasa_tlx_all_time: true } : {}),
        ...(inactiveDays ? { inactive_days: inactiveDays } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getEngagementUtilization = async (
    {
      clinicIds,
      year,
      month,
      startDate,
      endDate,
      startMonth,
      endMonth,
      allTime,
    }: IGetEngagementUtilizationParams,
    signal?: AbortSignal
  ): Promise<IEngagementUtilizationResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityEngagementUtilization,
      {
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
        ...(allTime
          ? { all_time: true }
          : {
              ...(year ? { year } : {}),
              ...(month ? { month } : {}),
              ...(startDate ? { start_date: startDate } : {}),
              ...(endDate ? { end_date: endDate } : {}),
              ...(startMonth ? { start_month: startMonth } : {}),
              ...(endMonth ? { end_month: endMonth } : {}),
            }),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getPtoRecords = async (
    {
      providerUserId,
      clinicIds,
      startMonth,
      endMonth,
      includeDeleted,
    }: IGetCompassusPtoRecordsParams,
    signal?: AbortSignal
  ): Promise<ICompassusPtoRecordsResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityPtoRecords,
      {
        ...(providerUserId ? { provider_user_id: providerUserId } : {}),
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
        // The backend requires start_month/end_month as a pair (like
        // start_date/end_date); only send them when both are present.
        ...(startMonth && endMonth
          ? { start_month: startMonth, end_month: endMonth }
          : {}),
        ...(includeDeleted ? { include_deleted: true } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const createPtoRecord = async (
    payload: ICreateCompassusPtoRecordPayload
  ): Promise<ICompassusPtoRecord> =>
    POST(APIRoutes.apiCompassusProductivityPtoRecords, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const updatePtoRecord = async (
    ptoRecordId: number,
    payload: IUpdateCompassusPtoRecordPayload
  ): Promise<ICompassusPtoRecord> =>
    PATCH(
      APIRoutes.apiCompassusProductivityPtoRecord.replace(
        "{pto_record_id}",
        ptoRecordId.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getMonthlyRecords = async (
    {
      clinicIds,
      startMonth,
      endMonth,
      providerUserId,
      includeDeleted,
    }: IGetCompassusMonthlyRecordsParams,
    signal?: AbortSignal
  ): Promise<ICompassusMonthlyRecordsResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityMonthlyRecords,
      {
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
        ...(startMonth ? { start_month: startMonth } : {}),
        ...(endMonth ? { end_month: endMonth } : {}),
        ...(providerUserId ? { provider_user_id: providerUserId } : {}),
        ...(includeDeleted ? { include_deleted: true } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const createMonthlyRecord = async (
    payload: ICreateCompassusMonthlyRecordPayload
  ): Promise<ICompassusMonthlyRecord> =>
    POST(APIRoutes.apiCompassusProductivityMonthlyRecords, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const updateMonthlyRecord = async (
    monthlyRecordId: number,
    payload: IUpdateCompassusMonthlyRecordPayload
  ): Promise<ICompassusMonthlyRecord> =>
    PATCH(
      APIRoutes.apiCompassusProductivityMonthlyRecord.replace(
        "{monthly_record_id}",
        monthlyRecordId.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getMonthlyAssumptions = async (
    { startMonth, endMonth }: IGetCompassusMonthlyAssumptionsParams,
    signal?: AbortSignal
  ): Promise<ICompassusMonthlyAssumptionsResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityMonthlyAssumptions,
      {
        ...(startMonth ? { start_month: startMonth } : {}),
        ...(endMonth ? { end_month: endMonth } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const createMonthlyAssumption = async (
    payload: ICreateCompassusMonthlyAssumptionPayload
  ): Promise<ICompassusMonthlyAssumption> =>
    POST(APIRoutes.apiCompassusProductivityMonthlyAssumptions, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const updateMonthlyAssumption = async (
    assumptionId: number,
    payload: IUpdateCompassusMonthlyAssumptionPayload
  ): Promise<ICompassusMonthlyAssumption> =>
    PATCH(
      APIRoutes.apiCompassusProductivityMonthlyAssumption.replace(
        "{assumption_id}",
        assumptionId.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const updateProviderProfile = async (
    providerUserId: number,
    payload: IUpdateCompassusProviderProfilePayload
  ): Promise<ICompassusProviderProfile> =>
    PATCH(
      APIRoutes.apiCompassusProductivityProviderProfile.replace(
        "{provider_user_id}",
        providerUserId.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  // Shared date/month-window query params for the self-scoped NP endpoints.
  const monthWindowParams = ({
    userId,
    year,
    month,
    startDate,
    endDate,
    startMonth,
    endMonth,
  }: IGetNpProductivityMeParams) => ({
    ...(userId ? { user_id: userId } : {}),
    ...(year ? { year } : {}),
    ...(month ? { month } : {}),
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(startMonth ? { start_month: startMonth } : {}),
    ...(endMonth ? { end_month: endMonth } : {}),
  });

  const getMyProductivity = async (
    params: IGetNpProductivityMeParams,
    signal?: AbortSignal
  ): Promise<INpProductivityMeResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityMe,
      monthWindowParams(params),
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getMySchedule = async (
    { userId, startDate, endDate }: IGetNpScheduleParams,
    signal?: AbortSignal
  ): Promise<INpScheduleResponse> =>
    GET(
      APIRoutes.apiCompassusNpMySchedule,
      {
        ...(userId ? { user_id: userId } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getMyPatients = async (
    params: IGetNpPatientsParams,
    signal?: AbortSignal
  ): Promise<INpPatientsResponse> =>
    GET(
      APIRoutes.apiCompassusNpMyPatients,
      monthWindowParams(params),
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getNasaTlxFeedback = async (
    { clinicIds }: IGetNasaTlxFeedbackParams,
    signal?: AbortSignal
  ): Promise<INasaTlxFeedbackResponse> =>
    GET(
      APIRoutes.apiCompassusProductivityNasaTlxFeedback,
      {
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  const getFacilityNpPatients = async (
    {
      clinicIds,
      year,
      month,
      startDate,
      endDate,
      startMonth,
      endMonth,
    }: IGetFacilityNpPatientsParams,
    signal?: AbortSignal
  ): Promise<IFacilityNpPatientsResponse> =>
    GET(
      APIRoutes.apiCompassusNpPatients,
      {
        ...(clinicIds && clinicIds.length
          ? { clinic_ids: clinicIds.join(",") }
          : {}),
        ...(year ? { year } : {}),
        ...(month ? { month } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(startMonth ? { start_month: startMonth } : {}),
        ...(endMonth ? { end_month: endMonth } : {}),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
      signal
    );

  return {
    getProductivity,
    getProductivitySummary,
    getProductivityProviders,
    getProductivityUtilization,
    getEngagementUtilization,
    getPtoRecords,
    createPtoRecord,
    updatePtoRecord,
    getMonthlyRecords,
    createMonthlyRecord,
    updateMonthlyRecord,
    getMonthlyAssumptions,
    createMonthlyAssumption,
    updateMonthlyAssumption,
    updateProviderProfile,
    getMyProductivity,
    getMySchedule,
    getMyPatients,
    getNasaTlxFeedback,
    getFacilityNpPatients,
  };
};
